"use client";

import React from "react";
import { MdArrowLeft, MdArrowRight } from "react-icons/md";
import { ExternalLinkIcon, SearchIcon } from "lucide-react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";

export type RowObjCommande = {
  number: string;
  _id: string;
  clientId: string;
  createdAt: Date;
  totalTTC: number;
  totalHT: number;
  total: number;
};

const columnHelper = createColumnHelper<RowObjCommande>();
const formatCurrency = (v: number) => Intl.NumberFormat("fr-FR").format(v) + " €";

export default function CommandeTable(props: {
  tableData: RowObjCommande[];
  inputValue: string;
  setInputValue: (value: string) => void;
  isLoading: boolean;
  currentPage: number;
  totalPage: number;
  hasNextPage: boolean;
  handleFetchNextPage: () => void;
  handleFetchPrevPage: () => void;
  handleTabChange: (value: boolean) => void;
  expired: boolean;
}) {
  const { tableData, handleFetchNextPage, hasNextPage, handleFetchPrevPage, totalPage, currentPage, handleTabChange, expired, isLoading } = props;
  const [sorting, setSorting] = React.useState<SortingState>([]);

  const columns = [
    columnHelper.accessor("number", {
      id: "N°",
      header: () => <span>N°</span>,
      cell: (info) => <span className="font-mono text-xs text-gray-500">{info.getValue() || "-"}</span>,
    }),
    columnHelper.accessor("clientId", {
      id: "Client",
      header: () => <span>Client</span>,
      cell: (info) => <span className="font-medium text-gray-900">{info.getValue() || "-"}</span>,
    }),
    columnHelper.accessor("createdAt", {
      id: "Date",
      header: () => <span>Date</span>,
      cell: (info) => <span className="text-gray-600">{new Date(info.getValue()).toLocaleDateString("fr-FR")}</span>,
    }),
    columnHelper.accessor("totalHT", {
      id: "HT",
      header: () => <span>HT</span>,
      cell: (info) => <span className="text-gray-600">{info.getValue() ? formatCurrency(info.getValue()) : "-"}</span>,
    }),
    columnHelper.accessor("totalTTC", {
      id: "TTC",
      header: () => <span>TTC</span>,
      cell: (info) => <span className="font-medium text-gray-900">{info.getValue() ? formatCurrency(info.getValue()) : "-"}</span>,
    }),
    columnHelper.accessor("_id", {
      id: "actions",
      header: () => <span></span>,
      cell: (info) => (
        <a href={`/admin/commande/${info.getValue()}`} className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600">
          <ExternalLinkIcon className="h-4 w-4" />
        </a>
      ),
    }),
  ];

  const table = useReactTable({ data: tableData, columns, state: { sorting }, onSortingChange: setSorting, getCoreRowModel: getCoreRowModel(), getSortedRowModel: getSortedRowModel() });

  return (
    <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
        <h3 className="text-base font-semibold text-gray-900">Commandes</h3>
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher..."
            className="h-8 w-48 rounded-lg border border-gray-200 bg-white pl-9 pr-3 text-xs outline-none transition-colors focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            value={props.inputValue}
            onChange={(e) => props.setInputValue(e.target.value)}
          />
        </div>
      </div>

      <div className="border-b border-gray-100 px-5">
        <div className="flex gap-0">
          <button
            onClick={() => handleTabChange(false)}
            className={`border-b-2 px-4 py-2.5 text-xs font-medium transition-colors ${!expired ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700"}`}
          >
            Actives
          </button>
          <button
            onClick={() => handleTabChange(true)}
            className={`border-b-2 px-4 py-2.5 text-xs font-medium transition-colors ${expired ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700"}`}
          >
            Terminées
          </button>
        </div>
      </div>

      <div className={`transition-opacity duration-150 ${isLoading ? "opacity-50" : "opacity-100"}`}>
        {tableData.length === 0 ? (
          <div className="py-16 text-center"><p className="text-sm text-gray-400">Aucune commande trouvée</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>{table.getHeaderGroups().map((hg) => (<tr key={hg.id}>{hg.headers.map((header) => (<th key={header.id} onClick={header.column.getToggleSortingHandler()} className="cursor-pointer px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-400">{flexRender(header.column.columnDef.header, header.getContext())}</th>))}</tr>))}</thead>
              <tbody className="divide-y divide-gray-50">{table.getRowModel().rows.map((row) => (<tr key={row.id} className="transition-colors hover:bg-gray-50/50">{row.getVisibleCells().map((cell) => (<td key={cell.id} className="whitespace-nowrap px-5 py-3 text-sm">{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>))}</tr>))}</tbody>
            </table>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-gray-100 px-5 py-3">
        <span className="text-xs text-gray-400">Page {currentPage} sur {totalPage}</span>
        <div className="flex items-center gap-1">
          <button onClick={handleFetchPrevPage} disabled={currentPage <= 1} className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 disabled:opacity-30"><MdArrowLeft className="h-5 w-5" /></button>
          <button onClick={handleFetchNextPage} disabled={!hasNextPage} className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 disabled:opacity-30"><MdArrowRight className="h-5 w-5" /></button>
        </div>
      </div>
    </div>
  );
}
